import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LoggerService } from './logger.service';

export interface PerformanceMetrics {
  pageLoad: number;
  domContentLoaded: number;
  firstContentfulPaint?: number;
  largestContentfulPaint?: number;
  firstInputDelay?: number;
  cumulativeLayoutShift?: number;
  memoryUsage?: number;
  timestamp: number;
  url: string;
}

@Injectable({
  providedIn: 'root'
})
export class PerformanceService {
  private metrics: PerformanceMetrics[] = [];
  private observer?: PerformanceObserver;

  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);

  constructor() {
    this.initializeObservers();
    this.trackNavigation();
  }

  private initializeObservers(): void {
    // Track navigation timing
    this.trackNavigationTiming();

    // Track Web Vitals if available
    if ('PerformanceObserver' in window) {
      this.trackWebVitals();
    }

    // Track memory usage
    this.trackMemoryUsage();
  }

  private trackNavigationTiming(): void {
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      const metrics: PerformanceMetrics = {
        pageLoad: navigation.loadEventEnd - navigation.fetchStart,
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
        timestamp: Date.now(),
        url: window.location.href
      };

      this.addMetrics(metrics);
    });
  }

  private trackWebVitals(): void {
    try {
      // Track Largest Contentful Paint (LCP)
      if ('PerformanceObserver' in window) {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          const lcp = lastEntry.startTime;

          this.updateLatestMetrics({ largestContentfulPaint: lcp });
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // Track First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            this.updateLatestMetrics({ firstInputDelay: entry.processingStart - entry.startTime });
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });

        // Track Cumulative Layout Shift (CLS)
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          this.updateLatestMetrics({ cumulativeLayoutShift: clsValue });
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });

        // Track First Contentful Paint (FCP)
        const fcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const fcp = entries[0].startTime;
          this.updateLatestMetrics({ firstContentfulPaint: fcp });
        });
        fcpObserver.observe({ entryTypes: ['paint'] });
      }
    } catch (error) {
      this.logger.warn('Failed to initialize Web Vitals tracking', error, 'PerformanceService');
    }
  }

  private trackMemoryUsage(): void {
    if ('memory' in performance) {
      setInterval(() => {
        const memory = (performance as any).memory;
        this.updateLatestMetrics({
          memoryUsage: memory.usedJSHeapSize
        });
      }, 30000); // Every 30 seconds
    }
  }

  private trackNavigation(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.logger.info('Navigation completed', {
        url: event.urlAfterRedirects,
        timestamp: Date.now()
      }, 'PerformanceService');

      // Clear metrics for new page
      setTimeout(() => {
        this.measurePageLoad();
      }, 100);
    });
  }

  private measurePageLoad(): void {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

    if (navigation) {
      const metrics: PerformanceMetrics = {
        pageLoad: navigation.loadEventEnd - navigation.fetchStart,
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
        timestamp: Date.now(),
        url: window.location.href
      };

      this.addMetrics(metrics);
    }
  }

  private addMetrics(metrics: Partial<PerformanceMetrics>): void {
    const fullMetrics: PerformanceMetrics = {
      pageLoad: 0,
      domContentLoaded: 0,
      timestamp: Date.now(),
      url: window.location.href,
      ...metrics
    };

    this.metrics.push(fullMetrics);

    // Keep only last 50 metrics
    if (this.metrics.length > 50) {
      this.metrics = this.metrics.slice(-50);
    }

    // Log slow pages
    if (fullMetrics.pageLoad > 3000) {
      this.logger.warn('Slow page load detected', {
        url: fullMetrics.url,
        loadTime: fullMetrics.pageLoad,
        domLoadTime: fullMetrics.domContentLoaded
      }, 'PerformanceService');
    }

    // Log performance data in development
    if (!this.isProduction()) {
      this.logger.debug('Performance metrics', fullMetrics, 'PerformanceService');
    }
  }

  private updateLatestMetrics(updates: Partial<PerformanceMetrics>): void {
    if (this.metrics.length > 0) {
      const latestMetrics = this.metrics[this.metrics.length - 1];
      Object.assign(latestMetrics, updates);
    } else {
      this.addMetrics(updates);
    }
  }

  public getMetrics(): PerformanceMetrics[] {
    return [...this.metrics];
  }

  public getLatestMetrics(): PerformanceMetrics | null {
    return this.metrics.length > 0 ? this.metrics[this.metrics.length - 1] : null;
  }

  public getAveragePageLoad(): number {
    if (this.metrics.length === 0) return 0;

    const total = this.metrics.reduce((sum, metric) => sum + metric.pageLoad, 0);
    return total / this.metrics.length;
  }

  public getMetricsByUrl(url: string): PerformanceMetrics[] {
    return this.metrics.filter(metric => metric.url === url);
  }

  public clearMetrics(): void {
    this.metrics = [];
  }

  public exportMetrics(): string {
    return JSON.stringify({
      exportDate: new Date().toISOString(),
      totalMetrics: this.metrics.length,
      averagePageLoad: this.getAveragePageLoad(),
      metrics: this.metrics
    }, null, 2);
  }

  private isProduction(): boolean {
    return !window.location.hostname.includes('localhost') &&
           !window.location.hostname.includes('127.0.0.1') &&
           !window.location.hostname.includes('.dev');
  }

  public measureFunction<T>(name: string, fn: () => T): T {
    const start = performance.now();
    const result = fn();
    const end = performance.now();
    const duration = end - start;

    this.logger.debug(`Function ${name} executed`, {
      functionName: name,
      duration: duration,
      timestamp: Date.now()
    }, 'PerformanceService');

    return result;
  }

  public async measureAsyncFunction<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const start = performance.now();
    const result = await fn();
    const end = performance.now();
    const duration = end - start;

    this.logger.debug(`Async function ${name} executed`, {
      functionName: name,
      duration: duration,
      timestamp: Date.now()
    }, 'PerformanceService');

    return result;
  }
}